import {
	type DraftEmissionRequestFormData,
	type EmissionRequestProductFormData,
	SupplierEmissionControllerApiFactory,
	SupplierEmissionsDataRequestControllerApiFactory,
} from '@api-client/api'
import { QueryKey } from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { useQueries } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { templatePage } from '../../route-definitions'
import { convertDraftToEmissionRequest } from './draftConversion'

const useProductRequestData = () => {
	const { requestId, productId } = useParams() as {
		requestId: string
		productId: string
	}
	const supplierEmissionControllerApi = useAsyncApi(
		SupplierEmissionControllerApiFactory
	)

	const supplierEmissionsDataRequestApi = useAsyncApi(
		SupplierEmissionsDataRequestControllerApiFactory
	)

	const [
		{ data: productData, isLoading: productLoading, error: productError },
		{ data: requestData, isLoading: requestLoading, error: requestError },
	] = useQueries({
		queries: [
			{
				queryKey: [
					QueryKey.GetEmissionRequestProductDetails,
					requestId,
					productId,
				],
				queryFn: () =>
					supplierEmissionControllerApi(api =>
						api.getEmissionRequestProductDetails(requestId, productId)
					),
			},
			{
				queryKey: [QueryKey.GetEmissionDataRequest, requestId],
				queryFn: () =>
					supplierEmissionsDataRequestApi(api =>
						api.getEmissionsDataRequest(requestId)
					),
			},
		],
	})

	const error = productError || requestError
	const aggregateLoading = productLoading || requestLoading

	const saveDraftData = useCallback(
		(emissionDraftData: DraftEmissionRequestFormData) =>
			supplierEmissionControllerApi(api =>
				api.persistDraftEmissionData(requestId, productId, {
					emissionDraftData,
				})
			),
		[requestId, productId, supplierEmissionControllerApi]
	)

	const submitEmissions = useCallback(
		async (
			formData: EmissionRequestProductFormData | DraftEmissionRequestFormData
		) => {
			await supplierEmissionControllerApi(api =>
				api.submitEmissionData(
					requestId,
					productId,
					convertDraftToEmissionRequest(formData)
				)
			)
		},
		[productId, requestId, supplierEmissionControllerApi]
	)

	return {
		saveDraftData,
		submitEmissions,
		productData: productData?.data,
		requestData: requestData?.data,
		loading: aggregateLoading,
		error,
	}
}

export const useEmissionFormParams = () => {
	const { requestId, productId, templateId } = useParams()
	if (templateId) {
		return {
			baseUrl: `${templatePage(templateId)}/emissions-data`,
			isTemplate: true,
			templateId,
		}
	}
	return {
		baseUrl: `/supplier/requests/${requestId}/products/${productId}`,
		isTemplate: false,
		productId,
		requestId,
	}
}

export default useProductRequestData
